import { useEffect } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useAddCountryMutation,
  useGetCountryByIdQuery,
  useUpdateCountryMutation,
} from '../../../apis/toolsApi';
import CrudLayout from '../../../layout/CRUDLayout';
import StatusSelect from '../../../ui-components/StatusSelect';

import type { Inputs } from '../model';

interface CountryFormProps {
  embedded?: boolean;
  countryId?: string;
  onClose?: () => void;
  onSuccess?: () => void;
}

const CountryForm = ({
  embedded = false,
  countryId,
  onClose,
  onSuccess,
}: CountryFormProps) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const resolvedId = countryId ?? id;
  const isEditMode = Boolean(resolvedId);

  const [addCountry, { isLoading }] = useAddCountryMutation();
  const [updateCountry, { isLoading: isUpdating }] = useUpdateCountryMutation();

  const { data: fetchedCountry, isLoading: isFetching } =
    useGetCountryByIdQuery(resolvedId || '', {
      skip: !resolvedId,
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    control,
  } = useForm<Inputs>({
    defaultValues: {
      country: '',
      countryCode: '',
      countryCodeIso3: '',
      dialCode: '',
      status: 'Active',
    },
  });

  useEffect(() => {
    if (fetchedCountry) {
      const data = fetchedCountry.data;
      setValue('country', data.country);
      setValue('countryCode', data.countryCode);
      setValue('countryCodeIso3', data.countryCodeIso3 ?? '');
      setValue('dialCode', data.dialCode || '');
      setValue('status', data.status);
    }
  }, [fetchedCountry, setValue]);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      isEditMode && resolvedId
        ? await updateCountry({
            formData: data,
            countryId: resolvedId,
          }).unwrap()
        : await addCountry(data).unwrap();

      if (embedded) {
        onSuccess?.();
        onClose?.();
      } else {
        navigate('/countries');
      }
    } catch (error) {
      console.log(error);
    }
  };

  if (isFetching) {
    return <div>Loading...</div>;
  }

  const formFields = (
    <>
      <div className="mb-4">
        <label htmlFor="country" className="label">
          Country Name <span className="required">*</span>
        </label>
        <input
          id="country"
          type="text"
          {...register('country', {
            required: 'Country Name is required',
            minLength: { value: 2, message: 'Must be at least 2 characters' },
            pattern: { value: /^[A-Za-z ]+$/, message: 'Invalid characters' },
          })}
          className="input"
          placeholder="Country Name"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        />
        {errors.country && (
          <p className="error-msg">{errors.country.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="countryCode" className="label">
          Country Code <span className="required">*</span>
        </label>
        <input
          type="text"
          {...register('countryCode', {
            required: 'Country Code is required',
            minLength: { value: 1, message: 'Min 1 character' },
            maxLength: { value: 3, message: 'Max 3 characters' },
            pattern: { value: /^[A-Za-z]+$/, message: 'Only letters allowed' },
          })}
          className="input"
          placeholder="Country Code"
        />
        {errors.countryCode && (
          <p className="error-msg">{errors.countryCode.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="countryIso3" className="label">
          Country ISO Code <span className="required">*</span>
        </label>
        <input
          type="text"
          {...register('countryCodeIso3', {
            required: 'Country ISO Code is required',
            minLength: { value: 2, message: 'At least 2 characters' },
            pattern: { value: /^[A-Za-z]+$/, message: 'Invalid format' },
          })}
          className="input"
          placeholder="Country ISO Code"
        />
        {errors.countryCodeIso3 && (
          <p className="error-msg">{errors.countryCodeIso3.message}</p>
        )}
      </div>

      <div className="mb-4">
        <label htmlFor="dialCode" className="label">
          Dial Code <span className="required">*</span>
        </label>
        <input
          type="text"
          {...register('dialCode', {
            required: 'Dial Code is required',
            minLength: { value: 2, message: 'Min 2 characters' },
            pattern: { value: /^[+0-9]+$/, message: 'Invalid format' },
          })}
          className="input"
          placeholder="Dial Code"
        />
        {errors.dialCode && (
          <p className="error-msg">{errors.dialCode.message}</p>
        )}
      </div>

      <div className="md:col-span-1">
        <StatusSelect
          label="Status"
          name="status"
          control={control}
          error={errors.status as any}
        />
      </div>
    </>
  );

  if (embedded) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {formFields}
        <div className="flex justify-end gap-3 pt-4 border-t border-neutral-100 dark:border-neutral-800 mt-4">
          <button
            type="button"
            onClick={() => (onClose ? onClose() : navigate('/countries'))}
            disabled={isLoading || isUpdating}
            className="px-6 py-2.5 rounded-lg text-sm font-bold text-neutral-700 dark:text-neutral-300 border border-neutral-200 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-900 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading || isUpdating}
            className={`px-8 py-2.5 rounded-lg text-sm font-bold transition-all text-neutral-700 tracking-wide ${
              isLoading || isUpdating
                ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-400 cursor-not-allowed'
                : 'bg-brand-600 hover:bg-brand-700 text-white shadow-lg shadow-brand-500/20 hover:shadow-brand-500/40'
            }`}
          >
            Save
          </button>
        </div>
      </form>
    );
  }

  return (
    <CrudLayout<Inputs>
      id={resolvedId}
      topTitle="Country Management"
      title={isEditMode ? 'Update Country' : 'Add Country'}
      backTo="/countries"
      isLoading={isLoading}
      isUpdating={isUpdating}
      onSubmit={onSubmit}
      handleSubmit={handleSubmit}
      formUiFormat="two-column"
    >
      {formFields}
    </CrudLayout>
  );
};

export default CountryForm;
